// ─────────────────────────────────────────────────────────────
// TraderSentimentWidget
//
// Drops into the right column of HorizontalDashboard.js
// directly below <MarketSentimentWidget />.
//
// Usage:
//   import { TraderSentimentWidget } from
//     "../../components/widgets/TraderSentimentWidget";
//
//   <TraderSentimentWidget
//     accountTrades={accountTrades}
//     accountRules={accountRules}
//     profile={profile}
//     totalEquity={totalEquity}
//     startingBalance={startingBalance}
//   />
//
// Signals (graceful degradation — missing data dims the signal
// and redistributes weight to what's available):
//
//   Win rate        40% weight  — from accountTrades (wins/total)
//   Rule adherence  30% weight  — from discipline_score on trades
//                                 (requires trades with scores)
//   Drawdown room   20% weight  — requires maxDailyDrawdown rule
//                                 or profile.max_daily_drawdown
//   Avg R:R         10% weight  — from trades with entry_price +
//                                 stop_loss, or t.risk field
// ─────────────────────────────────────────────────────────────

import { useMemo } from "react";
import { C } from "../../lib/colors";

// ── Score calculation ────────────────────────────────────────

function calcTraderSentiment({ accountTrades, accountRules, profile, totalEquity, startingBalance }) {
  const trades = Array.isArray(accountTrades) ? accountTrades : [];

  // ── Win rate (always available if trades exist) ──────────
  const totalTrades = trades.length;
  const wins        = trades.filter(t => t.result === "Win").length;
  const winRate     = totalTrades > 0 ? (wins / totalTrades) * 100 : null;

  // ── Rule adherence via discipline_score (0–10 → 0–100) ──
  const scoredTrades = trades.filter(t => t.discipline_score != null);
  const hasRuleData  = scoredTrades.length > 0;
  const ruleAdherence = hasRuleData
    ? (scoredTrades.reduce((s, t) => s + (t.discipline_score || 0), 0) / scoredTrades.length) * 10
    : null;

  // ── Drawdown room ────────────────────────────────────────
  const drawdownRule    = (accountRules || []).find(r => r.enabled && r.rule_type === "max_daily_drawdown");
  const maxDailyDrawdown = parseFloat(drawdownRule?.parameters?.max_drawdown)
    || parseFloat(profile?.max_daily_drawdown)
    || null;

  const hasRiskConfig = maxDailyDrawdown != null && startingBalance > 0;
  const todayLoss     = trades
    .filter(t => {
      const d = new Date();
      const today = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
      return String(t.trade_date).slice(0,10) === today;
    })
    .reduce((s, t) => s + Math.min(0, Number(t.pnl) || 0), 0);
  const ddRoom = hasRiskConfig
    ? Math.max(0, Math.min(100, ((maxDailyDrawdown + todayLoss) / maxDailyDrawdown) * 100))
    : null;

  // ── Avg R:R ──────────────────────────────────────────────
  const rrTrades = trades.filter(t => {
    if (t.entry_price && t.stop_loss) return Math.abs(t.entry_price - t.stop_loss) > 0;
    return t.risk != null && t.risk > 0;
  });
  const avgRR = rrTrades.length > 0
    ? rrTrades.reduce((s, t) => {
        let rr = null;
        if (t.entry_price && t.stop_loss) {
          const risk = Math.abs(t.entry_price - t.stop_loss);
          rr = risk > 0 ? (Number(t.pnl) || 0) / risk : null;
        }
        if (rr === null && t.risk) rr = (Number(t.pnl) || 0) / t.risk;
        return s + (rr ?? 0);
      }, 0) / rrTrades.length
    : null;

  // Normalise R:R to 0–100 (0→0, 1→50, 2→75, 3+→100)
  const rrNorm = avgRR != null
    ? Math.min(100, Math.max(0, (avgRR / 3) * 100))
    : null;

  // ── Weighted composite ───────────────────────────────────
  let weights = { wr: 0.40, ra: 0.30, dd: 0.20, rr: 0.10 };

  if (!hasRuleData) {
    weights.wr += weights.ra * 0.5;
    weights.rr += weights.ra * 0.5;
    weights.ra  = 0;
  }
  if (!hasRiskConfig) {
    weights.wr += weights.dd;
    weights.dd  = 0;
  }

  // Normalise so weights always sum to 1
  const total = weights.wr + weights.ra + weights.dd + weights.rr;
  Object.keys(weights).forEach(k => (weights[k] /= total));

  const signals = [
    { key: "wr", value: winRate,       weight: weights.wr },
    { key: "ra", value: ruleAdherence, weight: weights.ra },
    { key: "dd", value: ddRoom,        weight: weights.dd },
    { key: "rr", value: rrNorm,        weight: weights.rr },
  ];

  const availableSignals = signals.filter(s => s.value != null);
  if (availableSignals.length === 0 || totalTrades === 0) return null;

  const score = Math.round(
    availableSignals.reduce((s, sig) => s + sig.value * sig.weight, 0)
  );

  return {
    score,
    winRate,
    ruleAdherence,
    ddRoom,
    avgRR,
    hasRuleData,
    hasRiskConfig,
    totalTrades,
  };
}

// ── Verdict label + color ────────────────────────────────────

function getVerdict(score) {
  if (score >= 75) return { label: "Locked In",  color: "#22c55e" };
  if (score >= 58) return { label: "In Rhythm",  color: "#84cc16" };
  if (score >= 42) return { label: "Steady",     color: "#f59e0b" };
  if (score >= 25) return { label: "Drifting",   color: "#f97316" };
  return              { label: "Off Track",   color: "#ef4444" };
}

// ── Gauge SVG ────────────────────────────────────────────────

function GaugeMini({ score, color }) {
  const ARC_FULL = 119; // path length for the semicircle at this size
  const filled   = (score / 100) * ARC_FULL;
  const angle    = (score / 100) * 180 - 90; // –90° = left, +90° = right

  return (
    <svg viewBox="0 0 90 52" width="84" height="48" aria-hidden="true">
      <defs>
        <linearGradient id="tsg" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%"   stopColor="#ef4444" />
          <stop offset="50%"  stopColor="#f59e0b" />
          <stop offset="100%" stopColor="#22c55e" />
        </linearGradient>
      </defs>
      {/* Track */}
      <path
        d="M 8 46 A 37 37 0 0 1 82 46"
        fill="none"
        stroke="#1a2030"
        strokeWidth="7"
        strokeLinecap="round"
      />
      {/* Fill */}
      <path
        d="M 8 46 A 37 37 0 0 1 82 46"
        fill="none"
        stroke="url(#tsg)"
        strokeWidth="7"
        strokeLinecap="round"
        strokeDasharray={`${filled} ${ARC_FULL - filled}`}
      />
      {/* Needle */}
      <line
        x1="45" y1="46" x2="45" y2="14"
        stroke="#e2e8f0"
        strokeWidth="2"
        strokeLinecap="round"
        style={{
          transformOrigin: "45px 46px",
          transform: `rotate(${angle}deg)`,
        }}
      />
      <circle cx="45" cy="46" r="3.5" fill={color} />
    </svg>
  );
}

// ── Signal bar row ───────────────────────────────────────────

function SignalRow({ label, value, displayValue, barColor, dimmed }) {
  const pct = value != null ? Math.max(0, Math.min(100, value)) : 0;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11, opacity: dimmed ? 0.4 : 1 }}>
      <span style={{ color: C.muted, width: 90, flexShrink: 0 }}>{label}</span>
      <div style={{ flex: 1, height: 3, background: "#1a2030", borderRadius: 2 }}>
        <div style={{ width: `${pct}%`, height: "100%", background: barColor, borderRadius: 2 }} />
      </div>
      <span style={{ color: "#8896b0", fontWeight: 600, width: 34, textAlign: "right" }}>
        {displayValue}
      </span>
    </div>
  );
}

// ── Main widget ──────────────────────────────────────────────

export function TraderSentimentWidget({
  accountTrades,
  accountRules,
  profile,
  totalEquity,
  startingBalance,
}) {
  const result = useMemo(
    () => calcTraderSentiment({ accountTrades, accountRules, profile, totalEquity, startingBalance }),
    [accountTrades, accountRules, profile, totalEquity, startingBalance]
  );

  // ── Empty state ──────────────────────────────────────────
  if (!result) {
    return (
      <div style={{
        background: "#111827",
        border: `1px solid ${C.border}`,
        borderRadius: 10,
        padding: "14px 16px",
      }}>
        <div style={{
          fontSize: 10, fontWeight: 700, letterSpacing: "0.1em",
          color: C.muted, textTransform: "uppercase", marginBottom: 10,
        }}>
          Trader Sentiment
        </div>
        <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.5 }}>
          Log trades to unlock your sentiment score.
        </div>
      </div>
    );
  }

  const { score, winRate, ruleAdherence, ddRoom, avgRR, hasRuleData, hasRiskConfig, totalTrades } = result;
  const verdict = getVerdict(score);
  const incomplete = !hasRuleData || !hasRiskConfig;

  return (
    <div style={{
      background: "#111827",
      border: `1px solid ${C.border}`,
      borderRadius: 10,
      padding: "14px 16px",
    }}>

      {/* Header */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        marginBottom: 12,
      }}>
        <div style={{
          fontSize: 10, fontWeight: 700, letterSpacing: "0.1em",
          color: C.muted, textTransform: "uppercase",
        }}>
          Trader Sentiment
        </div>
        <span style={{
          fontSize: 9, fontWeight: 700,
          background: "rgba(34,197,94,0.12)",
          color: "#22c55e",
          border: "1px solid rgba(34,197,94,0.22)",
          borderRadius: 3, padding: "1px 6px", letterSpacing: "0.06em",
        }}>
          NEW
        </span>
      </div>

      {/* Score row */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>

        {/* Score + label */}
        <div>
          <div style={{ fontSize: 32, fontWeight: 800, color: "#e2e8f0", lineHeight: 1 }}>
            {score}
          </div>
          <div style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>out of 100</div>
          <div style={{
            marginTop: 6, fontSize: 12, fontWeight: 700,
            color: verdict.color, letterSpacing: "0.05em",
          }}>
            {verdict.label}
          </div>
          <div style={{ fontSize: 10, color: C.muted }}>
            {totalTrades} trade{totalTrades !== 1 ? "s" : ""} analysed
          </div>
        </div>

        {/* Mini gauge */}
        <GaugeMini score={score} color={verdict.color} />
      </div>

      {/* Signal bars */}
      <div style={{ display: "flex", flexDirection: "column", gap: 7, borderTop: `1px solid #1a2030`, paddingTop: 10 }}>
        <SignalRow
          label="Win rate"
          value={winRate}
          displayValue={winRate != null ? `${Math.round(winRate)}%` : "—"}
          barColor="#22c55e"
          dimmed={false}
        />
        <SignalRow
          label="Rule adherence"
          value={ruleAdherence}
          displayValue={ruleAdherence != null ? `${Math.round(ruleAdherence)}%` : "—"}
          barColor="#3b82f6"
          dimmed={!hasRuleData}
        />
        <SignalRow
          label="Drawdown room"
          value={ddRoom}
          displayValue={ddRoom != null ? `${Math.round(ddRoom)}%` : "—"}
          barColor="#a855f7"
          dimmed={!hasRiskConfig}
        />
        <SignalRow
          label="Avg R:R"
          value={avgRR != null ? (avgRR / 3) * 100 : null}
          displayValue={avgRR != null ? avgRR.toFixed(1) : "—"}
          barColor="#f97316"
          dimmed={false}
        />
      </div>

      {/* Incomplete data nudge */}
      {incomplete && (
        <div style={{
          marginTop: 10, fontSize: 11,
          color: "#f59e0b",
          background: "rgba(245,158,11,0.08)",
          border: "1px solid rgba(245,158,11,0.18)",
          borderRadius: 6, padding: "6px 10px", lineHeight: 1.45,
        }}>
          {!hasRuleData && !hasRiskConfig
            ? "Add risk settings and discipline scores to sharpen your score."
            : !hasRuleData
            ? "Score trades with a discipline rating to sharpen your score."
            : "Configure a daily drawdown rule to sharpen your score."}
        </div>
      )}

    </div>
  );
}
